import React, { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

const CodeBlock = ({ code, language }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="relative group">
            <button
                onClick={handleCopy}
                className="absolute top-2 right-2 z-10 p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-all duration-200 opacity-0 group-hover:opacity-100"
                title="Kopiuj do schowka"
            >
                {copied ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                )}
            </button>
            <div className="text-xs text-gray-400 mb-2 font-bold uppercase px-4 pt-3">{language === 'cpp' ? 'C++' : 'Python'}</div>
            <SyntaxHighlighter
                language={language}
                style={vscDarkPlus}
                customStyle={{
                    margin: 0,
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem',
                    paddingTop: '0.5rem',
                }}
            >
                {code}
            </SyntaxHighlighter>
        </div>
    );
};

const ZdolnySlazak = () => {
    const cppCode = `#include <iostream>
#include <vector>
using namespace std;
int main() {
    int n;
    cin >> n;
    vector<int> liczby;
    for (int i = 2; i <= n; i++) {
        bool czy_podzielna = false;
        for (int j : liczby)
            if (i % j == 0) {
                czy_podzielna = true;
                break;
            }
        if (!czy_podzielna)
            liczby.push_back(i);
    }
    for (int liczba : liczby)
        cout << liczba << " ";
    return 0;
}`;

    const pythonCode = `n = int(input())
liczby = []
for i in range(2, n + 1):
    czy_podzielna = False
    for j in liczby:
        if i % j == 0:
            czy_podzielna = True
            break
    if not czy_podzielna:
        liczby.append(i)
for liczba in liczby:
    print(liczba, end=' ')`;

    return (
        <div className="bg-gray-50 dark:bg-gray-900 pt-24 pb-12">
            <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-700 to-indigo-700 px-8 py-10 text-white flex flex-col md:flex-row justify-between items-center gap-8">
                        <div>
                            <h1 className="text-3xl md:text-4xl font-extrabold mb-4">zDolny Ślązak - Informatyka</h1>
                            <p className="text-blue-100 text-lg">Dolnośląski Konkurs Informatyczny dla uczniów szkół podstawowych</p>
                        </div>
                        <img src="/zdolnys.svg" alt="Logo zDolny Ślązak" className="h-32 w-auto flex-shrink-0 animate-breathing-bloom-image" />
                    </div>

                    <div className="p-8 md:p-12 space-y-10">
                        {/* O konkursie */}
                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                                <span className="bg-blue-100 text-blue-700 p-2 rounded-lg mr-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </span>
                                O konkursie
                            </h2>
                            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                                "zDolny Ślązak" to prestiżowy konkurs przedmiotowy dla uczniów klas IV-VIII szkół podstawowych z województwa dolnośląskiego.
                                Celem konkursu jest rozwijanie zainteresowań informatycznych, promowanie talentów oraz wyłanianie uczniów uzdolnionych w dziedzinie informatyki.
                            </p>
                        </section>

                        {/* Harmonogram */}
                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                                <span className="bg-indigo-100 text-indigo-700 p-2 rounded-lg mr-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                </span>
                                Harmonogram 2025/2026
                            </h2>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse rounded-lg overflow-hidden">
                                    <thead className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 uppercase text-sm font-semibold">
                                        <tr>
                                            <th className="p-4">Etap</th>
                                            <th className="p-4">Data</th>
                                            <th className="p-4">Godzina</th>
                                            <th className="p-4">Czas trwania</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 text-gray-700 dark:text-gray-300">
                                        <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                            <td className="p-4 font-medium">Szkolny</td>
                                            <td className="p-4">28 października 2025</td>
                                            <td className="p-4">10:00</td>
                                            <td className="p-4">60 minut</td>
                                        </tr>
                                        <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                            <td className="p-4 font-medium">Powiatowy</td>
                                            <td className="p-4">28 listopada 2025</td>
                                            <td className="p-4">12:00</td>
                                            <td className="p-4">90 minut</td>
                                        </tr>
                                        <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                            <td className="p-4 font-medium">Wojewódzki</td>
                                            <td className="p-4">6 marca 2026</td>
                                            <td className="p-4">12:00</td>
                                            <td className="p-4">210 minut</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 italic">
                                * Rejestracja szkół do 30 września 2025 r. przez stronę zdolnyslazak.edu.pl
                            </p>
                        </section>

                        {/* Zakres wiedzy */}
                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                                <span className="bg-violet-100 text-violet-700 p-2 rounded-lg mr-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                    </svg>
                                </span>
                                Zakres wiedzy i umiejętności
                            </h2>
                            <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-6 border border-gray-100 dark:border-gray-700">
                                <ul className="space-y-3 text-gray-700 dark:text-gray-300 list-disc list-inside">
                                    <li><strong className="text-gray-900 dark:text-white">Rozumienie i analiza problemów:</strong> myślenie logiczne, abstrakcyjne i algorytmiczne.</li>
                                    <li><strong className="text-gray-900 dark:text-white">Programowanie:</strong> tworzenie i modyfikowanie programów (w tym sterowanie robotami), rozwiązywanie problemów za pomocą algorytmów.</li>
                                    <li><strong className="text-gray-900 dark:text-white">Aplikacje użytkowe:</strong> biegłe posługiwanie się edytorami tekstu, arkuszami kalkulacyjnymi, programami do prezentacji i grafiki.</li>
                                    <li><strong className="text-gray-900 dark:text-white">Technologie webowe:</strong> tworzenie prostych stron internetowych z wykorzystaniem HTMLa i CSSa.</li>
                                    <li><strong className="text-gray-900 dark:text-white">Sieci komputerowe:</strong> znajomość podstaw działania sieci lokalnych i Internetu, bezpieczne korzystanie z zasobów sieciowych.</li>
                                    <li><strong className="text-gray-900 dark:text-white">Bezpieczeństwo i prawo:</strong> przestrzeganie zasad netykiety, ochrona prywatności, poszanowanie praw autorskich i licencji oprogramowania.</li>
                                </ul>
                            </div>
                        </section>

                        {/* Instrukcja Powiatowa */}
                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                                <span className="bg-red-100 text-red-700 p-2 rounded-lg mr-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </span>
                                Instrukcja przed etapem powiatowym
                            </h2>
                            <div className="space-y-8 text-gray-700 dark:text-gray-300 leading-relaxed">
                                <div>
                                    <p className="mb-4">
                                        Na etapie powiatowym zawodnicy rozwiązują test, w którym należy odpowiedzieć na pytania dotyczące programów komputerowych w językach C++ i Python.
                                        Na rozwiązanie testu zawodnicy mają <strong>90 minut</strong>.
                                    </p>
                                    <p>
                                        Każde zadanie będzie zawierało kod zapisany w C++ i jego równoważny wariant w Pythonie.
                                        Wystarczy znajomość jednego z języków.
                                        Podczas zawodów zawodnicy mogą używać Internetu jedynie w celu pracy w internetowym środowisku uruchomieniowym <strong>OnlineGDB</strong>.
                                    </p>
                                </div>

                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Instrukcja obsługi OnlineGDB</h3>
                                    <ul className="list-disc list-inside space-y-2 bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl">
                                        <li>Wejdź na stronę <a href="https://onlinegdb.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">onlinegdb.com</a>.</li>
                                        <li>Wybierz język (<strong>C++23</strong> lub <strong>Python 3</strong>) w prawym górnym rogu (lista <em>Language</em>).</li>
                                        <li>Wpisz lub wklej kod programu w głównym oknie edytora.</li>
                                        <li>Użyj przycisku <strong className="text-green-600">Run</strong> (na górze), aby uruchomić program.</li>
                                        <li>Dane wejściowe wprowadzaj w zakładce <strong>Input</strong> na dole ekranu (zaznacz opcję <em>Text</em>).</li>
                                        <li>Wynik działania programu pojawi się w oknie konsoli na dole.</li>
                                    </ul>
                                </div>

                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Przykładowe zadanie</h3>
                                    <p className="mb-4">Rozważmy następujący program, który czyta liczbę naturalną i wypisuje pewien ciąg liczb. Obie wersje (C++ i Pythonie) są równoważne.</p>

                                    <div className="grid md:grid-cols-2 gap-4 mb-6">
                                        <CodeBlock language="cpp" code={cppCode} />
                                        <CodeBlock language="python" code={pythonCode} />
                                    </div>

                                    <div className="space-y-4">
                                        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                                            <p className="font-semibold text-gray-900 dark:text-white">1.1. Co oblicza powyższy program?</p>
                                            <p className="text-gray-600 dark:text-gray-400 mt-1">Program generuje wszystkie <strong>liczby pierwsze</strong> mniejsze lub równe n.</p>
                                        </div>

                                        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                                            <p className="font-semibold text-gray-900 dark:text-white">1.2. Co wypisze program dla n=20?</p>
                                            <p className="text-gray-600 dark:text-gray-400 mt-1 font-mono">2 3 5 7 11 13 17 19</p>
                                        </div>

                                        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                                            <p className="font-semibold text-gray-900 dark:text-white">1.6. Ile liczb wypisze program dla n=200?</p>
                                            <p className="text-gray-600 dark:text-gray-400 mt-1">Odp: <strong>46</strong>. Można to sprawdzić modyfikując program, aby wypisywał <code>liczby.size()</code> (C++) lub <code>len(liczby)</code> (Python).</p>
                                        </div>

                                        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                                            <p className="font-semibold text-gray-900 dark:text-white">1.10. Ile liczb wypisze program dla n=2 000 000?</p>
                                            <p className="text-gray-600 dark:text-gray-400 mt-1">
                                                Podana implementacja jest zbyt wolna (złożoność O(n²)). Dla dużych danych należy użyć optymalizacji, np. <strong>Sita Eratostenesa</strong>.
                                                <br />Odp: <strong>148 933</strong>.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Linki */}
                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                                <span className="bg-green-100 text-green-700 p-2 rounded-lg mr-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                    </svg>
                                </span>
                                Przydatne linki
                            </h2>

                            <div className="space-y-6">

                                {/* Materiały do pobrania */}
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Materiały do pobrania (PDF)</h3>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        {/* Test próbny */}
                                        <div className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700">
                                            <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                Test próbny 2025/2026
                                            </h4>
                                            <div className="space-y-2">
                                                <a href="/2025-2026-INFORMATYKA PRÓBNY.pdf" target="_blank" rel="noopener noreferrer" className="flex items-center text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                    Zadania
                                                </a>
                                                <a href="/2025-2026-INFORMATYKA PRÓBNY-odp .pdf" target="_blank" rel="noopener noreferrer" className="flex items-center text-sm text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    Odpowiedzi
                                                </a>
                                            </div>
                                        </div>

                                        {/* Etap szkolny */}
                                        <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg p-4 border border-amber-200 dark:border-amber-700">
                                            <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                Etap szkolny 2025/2026
                                            </h4>
                                            <div className="space-y-2">
                                                <a href="/2025-2026-SZKOLNY INFORMATYKA.pdf" target="_blank" rel="noopener noreferrer" className="flex items-center text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                    Zadania
                                                </a>
                                                <a href="/2025-2026-SZKOLNY INFORMATYKA-odp.pdf" target="_blank" rel="noopener noreferrer" className="flex items-center text-sm text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    Odpowiedzi
                                                </a>
                                            </div>
                                        </div>

                                        {/* Instrukcja */}
                                        <div className="bg-gradient-to-br from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 rounded-lg p-4 border border-red-200 dark:border-red-700 md:col-span-2">
                                            <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                Instrukcja dla uczestników
                                            </h4>
                                            <a href="/instrukcja_informatyka.pdf" target="_blank" rel="noopener noreferrer" className="flex items-center text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                Pobierz instrukcję (PDF)
                                            </a>
                                        </div>

                                {/* Linki zewnętrzne */}
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <a href="https://zdolnyslazak.edu.pl" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-6 py-3 font-semibold text-white shadow-md hover:bg-blue-700 transition-all duration-300">
                                        Oficjalna strona konkursu
                                        <svg xmlns="http://www.w3.org/2000/svg" className="ml-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                    </a>

                                </div>
                                        
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ZdolnySlazak;


